import React from 'react';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { finishNestedFlow, submitData } from '../../actions/userData';
import { scrollTop } from '../../animation/scrollTo';
import { getMaritalStatus } from '../../calculations/userDataTransformer';
import { getAgeFromDob, getYearsUntil } from '../../utils/age';
import { validateHousing, validateInsurance } from '../flows/validate/expenseValidator';
import { ConnectedFlow } from './ConnectedFlow';

let flows = (userName, spouseName, maritalStatus) => ({
    housing: {
        questionData: [
            {
                questions: [
                    {
                        id: 'rentOrOwn', type: 'select', description: 'Rent or Own', text: `Do you own or rent your home?`, options: [
                            { id: 'rent', label: 'Rent' },
                            { id: 'own', label: 'Own' }
                        ]
                    },
                ],
                shouldShow: () => true,
                text: 'Enter details on your cost of housing',
                descriptor: 'Cost of Housing'
            },
            {
                questions: [
                    { id: 'ownMortgageAmount', type: 'endableIncome', description: 'Monthly Mortgage Payment', text: `What is your monthly house payment, not including your property taxes and homeowners insurance?`, help: "Enter your principal and interest payment only. If your monthly payment includes your homeowners and property tax subtract them from your payment" },
                    { id: 'ownHomeownersInsurance', type: 'dollar', description: 'Annual Homeowners Insurance', text: `How much do you pay annually for homeowners insurance?`, help: "This payment will continue indefinately" },
                    { id: 'ownPropertyTax', type: 'dollar', description: 'Annual Property Tax', text: `How much is your property tax annually?`, help: "This payment will continue indefinately" },
                ],
                shouldShow: (answers) => answers.rentOrOwn && answers.rentOrOwn[0]?.id === 'own',
            },
            {
                questions: [
                    { id: 'rentAmount', type: 'dollar', description: 'Rent Amount', text: `How much do you pay annually to rent?` },
                ],
                shouldShow: (answers) => answers.rentOrOwn && answers.rentOrOwn[0]?.id === 'rent',
            },
        ],
        validator: (answers) => validateHousing(answers),
        next: '/app/expenses/short-term-debts',
        previous: '/app/income/other',
    },
    'short-term-debts': {
        questionData: [
            {
                questions: [
                    { id: 'shortTermDebts', type: 'shortTermDebts', description: 'Short Term Debts', text: `Here are some categories of short term loans, please fill in as appropriate:`, help: "Enter your short term obligations and the expected date when the debt will be retired" },
                ],
                shouldShow: () => true,
                text: 'Short Term Debts and / or Expenses',
                descriptor: 'Cost of Housing'
            },
        ],
        next: '/app/expenses/household',
        previous: '/app/expenses/housing',
    },
    household: {
        questionData: [
            {
                questions: [
                    { id: 'generalExpenses', type: 'generalExpenses', description: 'General Expenses', text: `Here are some categories of expenses, please fill in as appropriate:` },
                ],
                shouldShow: () => true,
                text: 'Enter details on your current cost of living: ',
                descriptor: 'Current Cost of Living'
            },
        ],
        next: '/app/expenses/insurance',
        previous: '/app/expenses/short-term-debts',
    },
    insurance: {
        questionData: maritalStatus === 'married' ? makeMedicareQuestions('main', userName).concat(makeMedicareQuestions('spouse', spouseName)) : makeMedicareQuestions('main', userName),
        validator: (answers) => validateInsurance(answers, maritalStatus, userName, spouseName),
        next: '/app/portfolio',
        previous: '/app/expenses/household',
    },
});

function makeMedicareQuestions(prefix, name) {
    /*
    Medicare Flow
    
    If >= 65 {
        if !employed { // NEEDS SET IN ANSWERS
            Set taking via IRMA for current date
        // assuming they are employed
        } else { // CHECKED
            are you enrolled for Medicare?
            - Yes -> Set taking via IRMA for current Date // NEEDS SET IN ANSWERS
            - No -> Set taking via IRMA for retire date // NEEDS SET IN ANSWERS
        }
    } else {
        if !employed {
            Set taking via IRMA at age 65
        }
        // assuming they are employed
        if age(retireDate) >= 65 { 
            Will you turn Medicare on at age 65?? // CHECKED
            - Yes => Age 65 (set taking via IRMA)
                if has health insurance at work
                    Will this replace health insurance through work?
                        Yes => Just set for backend
                        No => How much will new health insurance at work be?
                            Number
            - No => When retire (set taking via IRMA)
        } else { // retire before age 65
            - Set taking via IRMA at age 65
            - Would you like to budget extra between retirement and Medicare coverage
        }
    }
        
    */



    return [
        {
            questions: [
                { id: prefix + 'HasExtraLifeInsurance', type: 'boolean', description: 'Has Extra Life Insurance', text: `Does ${name} have a Private Life Insurance policy outside of employment?`, help: "We need to determine if this expense will continue after retirement" },
            ],
            shouldShow: () => true,
            text: 'Enter details about additional insurance policies ' + name + ' may have:',
            descriptor: 'Extra Insurances'
        },
        {
            questions: [
                { id: prefix + 'LifeInsuranceExpense', type: 'dollar', description: 'Extra Life Insurance', text: `What is your annual premium for this policy?`, help: "We will guide you in determining if this is a prudent expense" },
                { id: prefix + 'LifeInsuranceEndDate', type: 'dob', description: 'Extra Life Insurance - End Date', text: `When does this policy mature?` },
                { id: prefix + 'LifeInsuranceDeathBenefit', type: 'dollar', description: 'Extra Life Insurance - Death Benefit', text: `What is the death benefit amount?` },
            ],
            shouldShow: (answers) => answers[prefix + 'HasExtraLifeInsurance'] === true,
        },
        {
            questions: [
                { id: prefix + 'HasHealthInsurance', type: 'boolean', description: 'Has Extra Health Insurance', text: `Does ${name} have a Private Health Insurance policy outside of employment?` },
            ],
            shouldShow: (answers) => true,
        },
        {
            questions: [
                { id: prefix + 'HealthInsurancePremium', type: 'dollar', description: 'Extra Health Insurance Premium', text: `What is the annual premium?` },
            ],
            shouldShow: (answers) => answers[prefix + 'HasHealthInsurance'] === true,
        },
        {
            // > 65 and is employed
            questions: [
                { id: prefix + 'TakingMedicare', type: 'boolean', description: 'Taking Medicare Curently', text: `Is ${name} presently receiving Medicare?` },
            ],
            shouldShow: (answers) => getAgeFromDob(answers[prefix + 'Dob']) >= 65 && answers[prefix + 'IsEmployed'],
        },
        {
            // will be employed during or > age 65
            questions: [
                { id: prefix + 'WillTurnOnAt65', type: 'boolean', description: 'Turn on at 65 despite still working', text: `Will ${name} file for Medicare at age 65?` }
            ],
            shouldShow: (answers) => getAgeFromDob(answers[prefix + 'Dob']) < 65 && answers[prefix + 'IsEmployed'] && getAgeFromDob(answers[prefix + 'Dob']) + getYearsUntil(answers[prefix + 'RetireDate']) >= 65
        },
        {
            // will retire before age 65
            questions: [
                { id: prefix + 'BudgetExtraForUncovered', type: 'boolean', description: 'Budget for Extra', text: `Would ${name} like to budget extra for private health insurance between retirement and Medicare coverage?` },
            ],
            shouldShow: (answers) => answers[prefix + 'IsEmployed'] && getAgeFromDob(answers[prefix + 'Dob']) + getYearsUntil(answers[prefix + 'RetireDate']) < 65,
        },
        {
            // will retire before age 65 and DOES want to budget extra between retire and medicare
            questions: [
                { id: prefix + 'BudgetExtraForUncoveredAmount', type: 'number', description: 'Extra Healthcare Cost', text: `How much would ${name} like to budget for this interim health insurance?` },
            ],
            shouldShow: (answers) => answers[prefix + 'BudgetExtraForUncovered'] === true && answers[prefix + 'IsEmployed'] && getAgeFromDob(answers[prefix + 'Dob']) + getYearsUntil(answers[prefix + 'RetireDate']) < 65,
        },
        {
            questions: [
                { id: prefix + 'SupplementalAmount', type: 'dollar', description: 'New Supplemental Insurance Cost', text: `What is ${name}'s annual Supplemental Health Insurance premium?` },
            ],
            shouldShow: (answers) => true,
        },
    ]
}

const ExpenseFlow = ({ userName, spouseName, maritalStatus, subTab, submitData, finishNestedFlow }) => {
    const history = useHistory();
    let expenseFlows = flows(userName, spouseName, maritalStatus)
    if (expenseFlows[subTab]) {
        return <ConnectedFlow finishFlow={() => {
            scrollTop();
            history.push(expenseFlows[subTab].next);
            submitData();
            finishNestedFlow('expenses', expenseFlows, subTab);
        }} flowMode={true}
            userName={userName}
            spouseName={spouseName}
            questionSections={expenseFlows[subTab].questionData}
            sectionValidator={expenseFlows[subTab].validator}
            goBack={() => {
                scrollTop();
                history.push(expenseFlows[subTab].previous);
                submitData();
            }} maritalStatus={maritalStatus} />
    }

    return <Redirect to='/app/expenses/housing' />
}

export default connect(state => {
    return {
        userName: state.userData.answers.mainUserName,
        spouseName: state.userData.answers.spouseUserName,
        maritalStatus: getMaritalStatus(state.userData.answers),
    }
}, {
    finishNestedFlow,
    submitData
})(ExpenseFlow);
